/**
 * CoverageQueueSection — pinned above the backlog on the dispatch day view.
 *
 * Surfaces jobs that lost their assigned tech to a call-in and still need a
 * human (GET /api/dispatch/coverage-queue). Each card is a drag-source using
 * the same `text/plain = jobId` protocol as BacklogPanel / MissedJobsSection,
 * so dropping it onto a tech's swimlane assigns it through the existing flow.
 * "Find coverage" opens the opportunity panel for that job via onFindCoverage.
 *
 * Renders nothing when the queue is empty.
 */
import { useQuery } from "@tanstack/react-query";
import { LifeBuoy, MapPin, UserX } from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export interface CoverageJob {
    id: string;
    title: string;
    status: string;
    due_by_date: string | null;
    customer_name: string | null;
    priority_tier: "T1" | "T2" | "T3" | null;
    site_name: string | null;
    site_city: string | null;
    site_region: string | null;
    latitude: number | null;
    longitude: number | null;
    call_in_date: string | null;
    absent_tech_name: string | null;
}

interface CoverageQueueResponse {
    jobs: CoverageJob[];
    total: number;
}

export function CoverageQueueSection({
    onJobClick,
    onJobDragStart,
    onJobDragEnd,
    onFindCoverage,
}: {
    onJobClick?: (jobId: string) => void;
    onJobDragStart?: (jobId: string) => void;
    onJobDragEnd?: () => void;
    onFindCoverage?: (job: CoverageJob) => void;
}) {
    const query = useQuery({
        queryKey: ["dispatch", "coverage-queue"],
        queryFn: () => api.get<CoverageQueueResponse>("/api/dispatch/coverage-queue"),
    });

    const jobs = query.data?.jobs ?? [];
    if (jobs.length === 0) return null;

    return (
        <section className="rounded-xl border border-l-4 border-l-rose-500 bg-rose-50/60 px-3 py-2.5 shadow-sm dark:bg-rose-950/20">
            <div className="mb-2 flex items-center gap-2">
                <LifeBuoy className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                <h2 className="text-sm font-bold text-foreground">Coverage Needed ({jobs.length})</h2>
            </div>
            <p className="mb-2 text-[11px] text-foreground/70">
                Jobs that lost their tech to a call-in. Drag onto a tech, or find nearby coverage.
            </p>
            <ul className="space-y-1.5">
                {jobs.map((j) => {
                    const location = [j.site_city, j.site_region].filter(Boolean).join(", ");
                    return (
                        <li
                            key={j.id}
                            draggable
                            onDragStart={(e) => {
                                e.dataTransfer.setData("text/plain", j.id);
                                e.dataTransfer.effectAllowed = "move";
                                onJobDragStart?.(j.id);
                            }}
                            onDragEnd={() => onJobDragEnd?.()}
                            className="group flex cursor-grab items-center gap-2 rounded-md border border-border/60 bg-card px-2.5 py-2 text-xs shadow-sm transition-all hover:-translate-y-px hover:border-primary/50 hover:shadow-md active:cursor-grabbing"
                        >
                            <div className="flex min-w-0 flex-1 flex-col">
                                <button
                                    type="button"
                                    className="min-w-0 truncate text-left font-medium hover:underline"
                                    onClick={() => onJobClick?.(j.id)}
                                >
                                    {j.title || "Untitled job"}
                                </button>
                                <span className="truncate text-[10px] text-foreground/70">
                                    {j.customer_name ?? j.site_name ?? "—"}
                                    {location ? ` · ${location}` : ""}
                                </span>
                                {j.absent_tech_name && (
                                    <span className="flex items-center gap-1 truncate text-[10px] text-rose-700 dark:text-rose-300">
                                        <UserX className="h-3 w-3 shrink-0" />
                                        {j.absent_tech_name} called in
                                    </span>
                                )}
                            </div>
                            {j.priority_tier && (
                                <Badge variant="outline" className="shrink-0 text-[10px]">
                                    {j.priority_tier}
                                </Badge>
                            )}
                            {onFindCoverage && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 shrink-0 gap-1 px-2 text-[11px]"
                                    onClick={() => onFindCoverage(j)}
                                    title="Find nearby coverage"
                                >
                                    <MapPin className="h-3 w-3" /> Find
                                </Button>
                            )}
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

export default CoverageQueueSection;
